import { VoyagePerformanceModelBase, SimulatedParamData, ParameterType, ParameterValue } from './voyage-performance-model';
import { PerformanceService, PerformanceData } from '../services/performance-service';
import { VesselsModel } from './vessels-model';
import { VoyagePlan } from './vessel-performance-model';
import { VoyageRouteLineFeature, VoyageRoutePointFeature } from './voyage-routing-model';
import { PlanSource } from "./voyage-plans-model";

const EcoVoyageParamNames: Map<ParameterType,string> = new Map([
    [ParameterType.SpeedOverGround, "opt.bsog"],
    [ParameterType.PropulsionPower, "opt.present_me_power"],
    [ParameterType.RPM, "opt.rpm"]
]);

export class VoyagePerformanceModelImpl extends VoyagePerformanceModelBase
{
    async syncData(imoNo: number, fromDate: Date, toDate: Date, plan: VoyagePlan|null)
    {
        const vessel = VesselsModel!.getVessel(imoNo);

        if (vessel && vessel.hasPAPI) {
            this.dispatchEvent(new Event('fetch-data-started'));
            this._perfData = await PerformanceService.fetchPerformanceData(imoNo, fromDate, toDate);
        } else {
            this._perfData = null;
        }
        this.dispatchEvent(new Event('papi-performance-updated'));
        
        this._plan = plan;
        this.dispatchEvent(new Event('performance-updated'));
    }

    extractOneParam(type: ParameterType, onlyPositiveValues: boolean = false): SimulatedParamData[]
    {
        const paramData: SimulatedParamData[] = [];
        if (this._plan)
        {
            const routeGeometry = this._plan.routes[this._plan.selectedRouteIndex].route_geometry;
            const values = <SimulatedParamData>({
                source: this._plan.source || PlanSource.VOYAGE_PLANNING,
                data: []
            });

            for (let i = 0; i < routeGeometry.length; i++)
            {
                const feature = routeGeometry[i];
                if (feature.geometry.type === "LineString")
                {
                    if (feature.properties.informationalWaypoints)
                    {
                        for (const point of (<VoyageRouteLineFeature>feature).properties.informationalWaypoints!)
                        {
                            const value: ParameterValue = {
                                timestamp: new Date(point.properties.timestamp!).getTime(),
                                value: 0
                            };
                            switch (type)
                            {
                                case ParameterType.SpeedOverGround:
                                    // Convert from m/s to knots
                                    value.value = point.properties.speed_over_ground * (60*60)/1852;
                                    break;
                                
                                case ParameterType.PropulsionPower:
                                    // Already in KW
                                    value.value = point.properties.propulsion_power;
                                    value.lower = point.properties.propulsion_power_lower;
                                    value.upper = point.properties.propulsion_power_upper;
                                    break;

                                case ParameterType.RPM:
                                    value.value = point.properties.main_engine_rpm;
                                    value.lower = point.properties.main_engine_rpm_lower;
                                    value.upper = point.properties.main_engine_rpm_upper;
                                    break;
                            }
                            
                            this._addToDataSet(values.data, this._roundValues(value));
                        }
                    }
                    else
                    {
                        // Pull timestamp from the start point
                        const value: ParameterValue = {
                            timestamp: new Date((<VoyageRoutePointFeature>routeGeometry[i-1]).properties.timestamp!).getTime(),
                            value: 0
                        };
                        switch (type)
                        {
                            case ParameterType.SpeedOverGround:
                                // Convert from m/s to knots
                                value.value = feature.properties.speed_over_ground * (60*60)/1852;
                                break;
                                
                            case ParameterType.PropulsionPower:
                                // Already in KW
                                value.value = feature.properties.propulsion_power;
                                value.lower = feature.properties.propulsion_power_lower;
                                value.upper = feature.properties.propulsion_power_upper;
                                break;

                            case ParameterType.RPM:
                                value.value = feature.properties.main_engine_rpm;
                                value.lower = feature.properties.main_engine_rpm_lower;
                                value.upper = feature.properties.main_engine_rpm_upper;
                                break;
                        }

                        // Omit missing values
                        if (!isNaN(value.value))
                        {
                            this._addToDataSet(values.data, this._roundValues(value));
                        }
                    }
                }
            }

            paramData.push(values);
        }

        return paramData;
    }

    extractOnePAPIParam(type: ParameterType, onlyPositiveValues: boolean = false): ParameterValue[]
    {
        if (!this._perfData) return [];

        const ts = this._perfData.ts;
        let vals: (number|null)[]|null;

        switch (type)
        {
            case ParameterType.PropulsionPower:
                vals = this._perfData.mepower;
                break;

            case ParameterType.RPM:
                vals = this._perfData.merpm;
                break;
            
            case ParameterType.SpeedOverGround:
                vals = this._perfData.sog;
                break;
        }

        if (!ts || !vals || ts.length !== vals.length) return [];

        const res: ParameterValue[] = [];
        for (let i=0; i<ts.length; i++)
        {
            let value = vals[i];

            if (onlyPositiveValues && (value === null || value < 0)) value = 0;

            // Omit missing values
            if (value !== null)
            {
                res.push({
                    timestamp: ts[i],
                    value: Math.round(value * 10) / 10
                });
            }
        }
        return res;
    }

    private _plan: VoyagePlan|null = null;
    private _perfData: PerformanceData|null = null;

    private _addToDataSet(values: ParameterValue[], value: ParameterValue): void
    {
        // Correct for overlapping timestamps (this can happen after a re-calc when a vessel is further along than expected)
        while (values.length > 0 && value.timestamp <= values[values.length-1].timestamp)
        {
            values.pop();
        }
        values.push(value);
    }

    private _roundValues(paramValue: ParameterValue): ParameterValue
    {
        paramValue.value = Math.round(paramValue.value * 10) / 10;
        if (paramValue.lower)
        {
            paramValue.lower = Math.round(paramValue.lower * 10) / 10;
        }
        if (paramValue.upper)
        {
            paramValue.upper = Math.round(paramValue.upper * 10) / 10;
        }
        return paramValue;
    }
}
